import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const CATEGORIES = [
  { name: 'General / Non-Contributing', price: 'R0', desc: 'Active account; no paid membership benefits', color: 'var(--color-grey)' },
  { name: 'Basic', price: 'R100/month', desc: 'Core membership benefits; no waiting period', color: '#1b7a30' },
  { name: 'Standard', price: 'R150/month', desc: 'Basic + accompaniment benefits after 3rd contribution', color: '#1a5fb4' },
  { name: 'VIP', price: 'R250/month', desc: 'Basic + Standard + paid transport support after 3rd contribution', color: 'var(--color-gold)' },
  { name: 'Once-Off Service', price: 'R380 once-off', desc: 'Assistance for a particular matter, ordinarily 3 months', color: '#7a4a1b' },
  { name: 'Volunteer', price: 'No fixed fee', desc: 'Participation according to organisational opportunities and approval', color: '#5b2b7a' },
];

export default function MembershipPolicy() {
  return (
    <div>
      <PageHeader
        eyebrow="Official Membership Policy"
        title="Singumkhonto Wabasebenzi NPC — Membership Policy"
        subtitle='Effective Date: 11 August 2026 — "Know Your Rights. Protect Your Rights. Stand Up For Your Rights."'
      />

      {/* Contribution Structure Table */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container">
          <span className="eyebrow">Official Membership Contribution Structure</span>
          <h2 style={{ marginTop: 12, marginBottom: 32 }}>Six ways to be part of the organisation</h2>
          <div className="grid grid-3">
            {CATEGORIES.map((c) => (
              <div className="card" key={c.name} style={{ borderLeft: `4px solid ${c.color}` }}>
                <h3 style={{ marginBottom: 4 }}>{c.name}</h3>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: c.color, marginBottom: 10 }}>{c.price}</div>
                <p style={{ fontSize: '0.9rem' }}>{c.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Link to="/register" className="btn btn-gold">Become a Member</Link>
          </div>
        </div>
      </section>

      {/* About & Position */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>About Singumkhonto Wabasebenzi NPC</h2>
          <p>
            Also known as The Spear of Workers, we are an independent workers' support
            organisation established to promote workers' rights awareness, education and
            access to procedural and paralegal assistance — including case assessment,
            document preparation, administrative assistance, referrals to appropriate
            institutions, and dispute-process accompaniment where applicable.
          </p>
          <div className="card" style={{ marginTop: 20 }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>Important organisational position</p>
            <p style={{ marginBottom: 0 }}>
              Singumkhonto Wabasebenzi NPC is <strong>not</strong> a trade union, a law firm,
              or a firm of attorneys, and is <strong>not affiliated</strong> with the CCMA or
              Department of Employment and Labour. We do not replace an attorney, advocate,
              trade union, or other legally authorised professional where such representation
              is required. Members remain responsible for their own legal decisions,
              submissions and cases.
            </p>
          </div>

          <h2 style={{ marginTop: 40 }}>Everyone Is Welcome to Register</h2>
          <p>
            You do not have to begin with a paid monthly package. Through our website you
            may register an account and become part of our community as a{' '}
            <strong>contributing member</strong> (Basic, Standard, VIP, or Once-Off Service)
            or as a <strong>general/non-contributing member</strong> — a registered account
            with no active paid membership. General members may later request permission to
            select a paid package when they're ready.
          </p>
          <p>
            Having a website account does not automatically activate paid membership
            benefits — your account status and selected package determine which benefits
            are available to you.
          </p>
        </div>
      </section>

      {/* Basic */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Basic Continuous Membership — R100/month</h2>
          <p>The organisation's standard continuous membership option, with <strong>no waiting period</strong>. Benefits include:</p>
          <ul>
            <li>Workers' rights education and guidance</li>
            <li>Case consultation, review, and procedural guidance</li>
            <li>Administrative assistance and document-related support within our mandate</li>
            <li>Assistance organising case information and evidence</li>
            <li>Guidance on appropriate labour institutions and procedures</li>
            <li>Member portal access</li>
          </ul>
          <p>Basic Membership does not include physical accompaniment. If an accompaniment service is needed outside this package, we'll advise you of the applicable arrangement first.</p>
        </div>
      </section>

      {/* Standard */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Standard Continuous Membership — R150/month</h2>
          <p>Includes all Basic benefits, plus accompaniment to workplace hearings, investigations, CCMA proceedings, and Labour Court sessions — subject to our mandate, availability, and procedural limitations.</p>
          <div className="card" style={{ marginTop: 16 }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>Province requirement</p>
            <p style={{ marginBottom: 0 }}>
              Physical accompaniment is currently only available where we have an
              operational office. <strong>As of this policy's effective date, Mpumalanga is
              the only province with an operational office</strong> for this purpose. We aim
              to reach roughly 100 members in a province before considering a new branch
              there, subject to our resources and capacity.
            </p>
          </div>
          <p style={{ marginTop: 16 }}><strong>Transport:</strong> under Standard Membership, you remain responsible for your own transport costs to hearings, investigations, CCMA proceedings, or Labour Court sessions.</p>
        </div>
      </section>

      {/* VIP */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>VIP Continuous Membership — R250/month</h2>
          <p>Basic + Standard benefits, plus <strong>paid accompaniment transport support</strong> — where physical accompaniment is approved and provided, we cover the applicable transport cost, subject to our procedures, availability, and reasonable limits.</p>
        </div>
      </section>

      {/* Waiting Period */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Standard & VIP Waiting Period</h2>
          <p>
            A new Standard or VIP member doesn't immediately receive the additional
            accompaniment benefits — those unlock after <strong>3 consecutive monthly
            contributions</strong>. Basic benefits are available immediately throughout.
          </p>
          <div className="grid grid-3" style={{ marginTop: 20 }}>
            <div className="card">
              <h3>Month 1</h3>
              <p style={{ fontSize: '0.9rem' }}>Contribution paid. Basic benefits active immediately. Additional Standard/VIP benefits not yet active.</p>
            </div>
            <div className="card">
              <h3>Month 2</h3>
              <p style={{ fontSize: '0.9rem' }}>Second contribution made. Basic benefits continue. Waiting period continues.</p>
            </div>
            <div className="card">
              <h3>Month 3</h3>
              <p style={{ fontSize: '0.9rem' }}>Third contribution made. Additional Standard/VIP accompaniment benefits become available.</p>
            </div>
          </div>
          <p style={{ marginTop: 16 }}><strong>Basic Membership has no waiting period</strong> — benefits apply immediately once the R100 contribution is successfully made.</p>
        </div>
      </section>

      {/* Once-Off */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Once-Off Service Membership — R380 once-off</h2>
          <p>
            Designed for workers who want assistance with a particular workplace matter
            without committing to continuous monthly membership. The standard period is{' '}
            <strong>three months</strong>, during which we may assist with your matter within
            our mandate. It's not intended as a permanent monthly arrangement.
          </p>
          <p>
            If your matter isn't resolved by the end of the three months, you may continue
            by selecting Basic, Standard, or VIP Membership — moving to Standard or VIP at
            that point still applies the 3-month waiting period described above (though
            Basic benefits remain available immediately either way).
          </p>

          <div className="card" style={{ marginTop: 20, borderLeft: '4px solid var(--color-gold)' }}>
            <p style={{ fontWeight: 700, marginBottom: 8 }}>Exception — workers who have lost their income</p>
            <p style={{ marginBottom: 0 }}>
              If you joined under the Once-Off Service Membership and are unemployed having
              lost your income, we may continue assisting with your matter beyond the
              standard three months until it's finalised, for as long as you remain
              unemployed and continue to qualify. If you obtain stable employment, this
              exception ends and you may need to select a continuous membership package to
              continue receiving assistance.
            </p>
          </div>
        </div>
      </section>

      {/* Missed Payments & Suspension */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Missed Monthly Contributions</h2>
          <p>
            You may miss up to <strong>two consecutive monthly contributions</strong> without
            immediate suspension, but your account must be brought up to date by the third
            month. For example: if Months 1 and 2 go unpaid, by Month 3 you must settle both
            outstanding months plus the current month's contribution.
          </p>

          <h2 style={{ marginTop: 32 }}>Suspension</h2>
          <p>
            If the fourth month begins with outstanding contributions still unsettled, the
            membership is <strong>suspended effective immediately</strong>. A suspended
            member is no longer entitled to active membership benefits — including for any
            new workplace problem arising during suspension. Your account remains on record
            until you choose to reactivate it.
          </p>

          <h2 style={{ marginTop: 32 }}>Reactivation</h2>
          <p>
            To reactivate a suspended membership, settle the outstanding contributions for
            your previous package, plus the current activation contribution for the package
            you're returning to. For example, if 3 months became outstanding:
          </p>
          <div className="grid grid-3" style={{ marginTop: 16 }}>
            <div className="card">
              <h3>Basic</h3>
              <p style={{ fontSize: '0.9rem' }}>3 × R100 = R300<br />+ R100 activation<br /><strong>Total: R400</strong></p>
            </div>
            <div className="card">
              <h3>Standard</h3>
              <p style={{ fontSize: '0.9rem' }}>3 × R150 = R450<br />+ R150 activation<br /><strong>Total: R600</strong></p>
            </div>
            <div className="card">
              <h3>VIP</h3>
              <p style={{ fontSize: '0.9rem' }}>3 × R250 = R750<br />+ R250 activation<br /><strong>Total: R1,000</strong></p>
            </div>
          </div>
          <p style={{ marginTop: 16, fontSize: '0.9rem', color: 'var(--color-grey)' }}>
            The exact amount owed will be confirmed against our financial records before
            reactivation.
          </p>
        </div>
      </section>

      {/* General Membership */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>General / Non-Contributing Membership</h2>
          <p>
            You may register on our website without selecting a paid package — whether
            you're still learning about us, considering membership, currently unable to
            afford a contribution, interested in volunteering, or simply want to stay
            connected. This does <strong>not</strong> automatically provide the paid benefits
            of Basic, Standard, or VIP Membership. You can request to update your account to
            a paid package whenever you're ready.
          </p>
        </div>
      </section>

      {/* Volunteering */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Volunteering</h2>
          <p>
            You don't need to be a paying member to volunteer with us. Opportunities may
            include community worker education, awareness campaigns, administration,
            community mobilisation, digital support, communication, research, event support,
            and more — subject to our needs, available positions, training requirements, and
            approval.
          </p>
          <div style={{ marginTop: 16 }}>
            <Link to="/volunteer" className="btn btn-outline-dark">Apply to Volunteer</Link>
          </div>
        </div>
      </section>

      {/* Referral Programme */}
      <section className="dark-section section">
        <div className="container" style={{ maxWidth: 820 }}>
          <span className="eyebrow">Referral Programme</span>
          <h2 style={{ marginTop: 12 }}>5% Monthly Recurring Commission</h2>
          <p>
            Eligible participants receive a unique referral link and/or code to share. Where
            a referred person successfully joins and remains an active contributing member,
            you may earn <strong>5% recurring commission</strong>, subject to successful
            attribution, confirmation the referred member is active, successful receipt of
            their contribution, our financial verification, and applicable programme rules
            (which may be amended in future).
          </p>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="section">
        <div className="container grid grid-2">
          <div>
            <h2>Member Responsibilities</h2>
            <ul>
              <li>Provide truthful, accurate information</li>
              <li>Keep contact details updated</li>
              <li>Provide accurate employment and case information</li>
              <li>Upload requested documents</li>
              <li>Pay membership contributions on time</li>
              <li>Keep login information secure</li>
              <li>Treat staff, volunteers and members respectfully</li>
              <li>Attend appointments or notify us when unable to</li>
              <li>Follow organisational procedures</li>
              <li>Avoid fraudulent or misleading information</li>
              <li>Understand membership doesn't guarantee a legal outcome</li>
              <li>Remain responsible for your own decisions and conduct</li>
            </ul>
          </div>
          <div>
            <h2>Our Responsibilities</h2>
            <ul>
              <li>Provide accurate workers' rights education</li>
              <li>Provide assistance within our organisational mandate</li>
              <li>Maintain member records</li>
              <li>Communicate important case updates</li>
              <li>Provide available member portal functionality</li>
              <li>Protect organisational and member information appropriately</li>
              <li>Provide membership information transparently</li>
              <li>Maintain appropriate financial and membership records</li>
              <li>Treat members fairly and respectfully</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Not a Guarantee */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Membership Is Not a Guarantee of Outcome</h2>
          <p>
            We provide support, education, guidance and assistance within our mandate.
            Membership does not guarantee winning a CCMA or Labour Court matter,
            reinstatement, compensation, settlement, a particular employer response, or any
            specific legal outcome. Final outcomes depend on employers, statutory
            institutions, courts, commissioners, evidence, applicable law, and other
            circumstances beyond our control.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="dark-section section">
        <div className="container" style={{ maxWidth: 820 }}>
          <span className="eyebrow">Organisational Disclaimer</span>
          <h2 style={{ marginTop: 12 }}>Our Position</h2>
          <p>
            Singumkhonto Wabasebenzi NPC (The Spear of Workers) is an independent workers'
            support organisation. We are not a trade union, law firm, or attorneys, and we
            are not affiliated with the CCMA or Department of Employment and Labour. Our
            role is workers' rights education, paralegal guidance, administrative
            assistance, case support, and other assistance within our organisational
            mandate. Where a matter requires professional legal representation or falls
            outside our mandate, you may be advised to seek an appropriately qualified legal
            professional or relevant institution.
          </p>
        </div>
      </section>

      {/* Closing */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820, textAlign: 'center' }}>
          <h2>Our Message to Every Worker</h2>
          <p>
            You may join us as a contributing member, a once-off service member, a
            general/non-contributing member, a volunteer — or simply as someone who wants to
            stay connected while deciding what role to play. There is a place for everyone
            who genuinely wants to contribute to the advancement of workers' rights.
          </p>
          <p style={{ fontStyle: 'italic', fontSize: '1.1rem', marginTop: 24 }}>
            "Know Your Rights. Protect Your Rights. Stand Up For Your Rights.<br />
            Together We Stand. Together We Win.<br />
            Stabbing Back Legally."
          </p>
          <div style={{ marginTop: 24 }}>
            <Link to="/register" className="btn btn-gold">Become a Member</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
